import { toConsumableArray as _toConsumableArray, slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2 } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import { ReactEditor } from 'slate-react';
import { Node, Point, Editor, Transforms, Element, Path } from 'slate';
import { Types } from './contants.js';
import cloneDeep from 'lodash/cloneDeep';
var LIST_TYPES = [Types.numberedList, Types.bulletedList];
var HOTKEYS = {
  'mod+b': 'bold',
  'mod+i': 'italic',
  'mod+u': 'underline',
  'mod+`': 'code'
};
var BLOCK_HTML_TAGS = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'blockquote', 'pre', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'section', 'article', 'aside', 'header', 'footer', 'nav', 'figure', 'figcaption', 'iframe'];
var VOID_ELEMENTS = ['img', 'hr', 'br'];
var HTML_TAGS = [].concat(BLOCK_HTML_TAGS, VOID_ELEMENTS, ['a', 'span', 'b', 'strong', 'i']);
/**
 * resetNodes resets the value of the editor. Pass in Node[] data to replace what's there.
 * It should be noted that passing the `at` parameter may cause a "Cannot resolve a DOM point from Slate point" error.
 */

var resetNodes = function resetNodes(editor) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  var children = _toConsumableArray(editor.children);

  children.forEach(function (node) {
    return editor.apply({
      type: 'remove_node',
      path: [0],
      node: node
    });
  });

  if (options.nodes) {
    var nodes = Node.isNode(options.nodes) ? [options.nodes] : options.nodes;
    nodes.forEach(function (node, i) {
      return editor.apply({
        type: 'insert_node',
        path: [i],
        node: node
      });
    });
  }

  var point = options.at && Point.isPoint(options.at) ? options.at : Editor.end(editor, []);

  if (point) {
    Transforms.select(editor, point);
  }
};

var isMarkActive = function isMarkActive(editor, format) {
  var marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

var isBlockActive = function isBlockActive(editor, format) {
  var nodeGen = Editor.nodes(editor, {
    match: function match(n) {
      return !Editor.isEditor(n) && Element.isElement(n) && n.type === format;
    }
  });
  var node = nodeGen.next();

  while (!node.done) {
    return true;
  }

  return false;
};

var toggleMark = function toggleMark(editor, format) {
  var isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

var toggleBlock = function toggleBlock(editor, format) {
  var isActive = isBlockActive(editor, format);
  var isList = LIST_TYPES.includes(format);
  Transforms.unwrapNodes(editor, {
    match: function match(n) {
      return LIST_TYPES.includes(!Editor.isEditor(n) && Element.isElement(n) && n.type);
    },
    split: true
  });
  var newProperties = {
    type: isActive ? Types.paragraph : isList ? Types.listItem : format
  };
  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    var block = {
      type: format,
      children: []
    };
    Transforms.wrapNodes(editor, block);
  }
};

var styleToString = function styleToString(style) {
  return Object.keys(style).reduce(function (acc, key) {
    return acc + key.split(/(?=[A-Z])/).join('-').toLowerCase() + ':' + style[key] + ';';
  }, '');
};

var toggleLink = function toggleLink(editor, url) {
  var selection = editor.selection;

  if (selection) {
    var _Editor$nodes = Editor.nodes(editor, {
      match: function match(n) {
        return !Editor.isEditor(n) && Element.isElement(n) && n.type === Types.link;
      }
    }),
        _Editor$nodes2 = _slicedToArray(_Editor$nodes, 1),
        link = _Editor$nodes2[0];

    if (link) {
      Transforms.unwrapNodes(editor, {
        match: function match(n) {
          return !Editor.isEditor(n) && Element.isElement(n) && n.type === Types.link;
        }
      });
    } else {
      var _link = {
        type: Types.link,
        url: url,
        children: []
      };
      Transforms.wrapNodes(editor, _link, {
        split: true,
        at: selection
      });
      Transforms.collapse(editor, {
        edge: 'end'
      });
    }
  }
};

var splitOnBlockElements = function splitOnBlockElements(htmlString) {
  var regex = new RegExp("(</?(?:".concat(BLOCK_HTML_TAGS.join('|'), ")[^>]*>)"), 'gi');
  return htmlString.split(regex).map(function (line) {
    if (regex.test(line)) {
      return line.trim();
    } else {
      return '  ' + line.trim();
    }
  }).filter(function (line) {
    return line !== '  ';
  });
};

var htmlStyleToReactStyle = function htmlStyleToReactStyle(htmlStyle) {
  var pairs = htmlStyle.split(';').filter(function (pair) {
    return pair.trim() !== '';
  });
  var styleObject = {};
  pairs.forEach(function (pair) {
    var _pair$split$map = pair.split(':').map(function (item) {
      return item.trim();
    }),
        _pair$split$map2 = _slicedToArray(_pair$split$map, 2),
        property = _pair$split$map2[0],
        value = _pair$split$map2[1];

    var camelCaseProperty = property.replace(/-([a-z])/g, function (g) {
      return g[1].toUpperCase();
    });
    var processedValue = value.replace(/(\d+)px/, '$1');
    styleObject[camelCaseProperty] = processedValue;
  });
  return styleObject;
};
/**
 * @param editor
 * @param url
 */


var insertImage = function insertImage(editor, url) {
  var selection = editor.selection;
  ReactEditor.focus(editor);
  var image = {
    type: Types.image,
    src: url,
    children: [{
      text: ''
    }]
  }; // eslint-disable-next-line no-extra-boolean-cast

  if (!!selection) {
    var _selection$focus;

    var _Editor$parent = Editor.parent(editor, (_selection$focus = selection.focus) === null || _selection$focus === void 0 ? void 0 : _selection$focus.path),
        _Editor$parent2 = _slicedToArray(_Editor$parent, 2),
        parentNode = _Editor$parent2[0],
        parentPath = _Editor$parent2[1];

    if (editor.isVoid(parentNode) || Node.string(parentNode).length) {
      // Insert the new image node after the void node or a node with content
      Transforms.insertNodes(editor, image, {
        at: Path.next(parentPath),
        select: true
      });
    } else {
      // If the node is empty, replace it instead
      Transforms.removeNodes(editor, {
        at: parentPath
      });
      Transforms.insertNodes(editor, image, {
        at: parentPath,
        select: true
      });
    }
  } else {
    // Insert the new image node at the bottom of the Editor when selection is falsey
    Transforms.insertNodes(editor, image);
    Transforms.insertNodes(editor, {
      type: Types.paragraph,
      children: [{
        text: ''
      }]
    });
  }
};

var isValidURL = function isValidURL(url, imageCheck) {
  var urlPattern = new RegExp('^(https?:\\/\\/)?' + // protocol
  '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.?)+[a-z]{2,}|' + // domain name
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
  '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
  '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
  '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator

  var imageExtensions = ['jpeg', 'jpg', 'png', 'gif'];

  if (!urlPattern.test(url)) {
    return false;
  }

  try {
    var _url = new URL(url);

    if (imageCheck) {
      var _url$pathname$split$p;

      if (!(_url !== null && _url !== void 0 && _url.pathname)) return false;
      var extension = (_url$pathname$split$p = _url.pathname.split('.').pop()) === null || _url$pathname$split$p === void 0 ? void 0 : _url$pathname$split$p.toLowerCase();
      return extension ? imageExtensions.includes(extension) : false;
    } else {
      return true;
    }
  } catch (_unused) {
    return false;
  }
};
/**
 * bring in attributes and the element, combine and filter, then return for a spread across
 * the tag. i. e. add the className or style to a div etc
 * @param attributes
 * @param element
 * @param filters (optional keys to filter from the returned object)
 */


var getAttrs = function getAttrs(attributes, element) {
  var filters = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : ['url', 'src', 'children'];

  var attrs = _objectSpread2(_objectSpread2({}, cloneDeep(attributes)), cloneDeep(element));

  return filterAttrs(attrs, filters);
};

var filterAttrs = function filterAttrs(attrsToFilter, keysToFilter) {
  return Object.keys(attrsToFilter).reduce(function (acc, key) {
    if (!keysToFilter.includes(key)) {
      if (key === 'class') {
        // change to react className
        // @ts-ignore
        acc.className = attrsToFilter[key]; // @ts-ignore

        delete acc["class"];
      } else if (key === 'style') {
        // change to react style object
        acc[key] = convertToReactStyles(attrsToFilter[key]);
      } else {
        // everything else except for what's been filtered out in the call
        acc[key] = attrsToFilter[key];
      }
    }

    return acc;
  }, {});
};

var convertToReactStyles = function convertToReactStyles(styleTag) {
  var newStyles = {};
  var stylesDeclarations = styleTag.split(';').filter(Boolean);
  stylesDeclarations.forEach(function (style) {
    var _style$split$map = style.split(':').map(function (item) {
      return item.trim();
    }),
        _style$split$map2 = _slicedToArray(_style$split$map, 2),
        property = _style$split$map2[0],
        value = _style$split$map2[1];

    var camelCaseProperty = property.replace(/-([a-z])/g, function (match, p1) {
      return p1.toUpperCase();
    });
    newStyles[camelCaseProperty] = value;
  });
  return newStyles;
};

export { BLOCK_HTML_TAGS, HOTKEYS, HTML_TAGS, VOID_ELEMENTS, getAttrs, htmlStyleToReactStyle, insertImage, isBlockActive, isMarkActive, isValidURL, resetNodes, splitOnBlockElements, styleToString, toggleBlock, toggleLink, toggleMark };